import React from 'react';
import {useTranslation} from 'react-i18next';

// Saisie du prono MI-TEMPS.
// - Le score final déjà saisi est rappelé en contexte (informatif).
// - Borne basse uniquement : htTeam >= scoreLive (on ne prédit pas sous le score déjà acquis),
//   pas de plafond — comme la saisie du score global.
const TeamPicker = ({label, logoUrl, value, min, onPlus, onMinus}) => {
    const minusDisabled = value <= min;
    return (
        <div className="flex flex-col items-center gap-2">
            <div className="flex items-center gap-1.5 mb-1">
                {logoUrl && <img src={logoUrl} alt="" className="w-5 h-5 object-contain rounded-full"/>}
                <span className="text-xs font-bold text-gray-500 uppercase truncate max-w-[80px]">{label}</span>
            </div>
            <button
                onClick={onPlus}
                className="w-11 h-11 rounded-full flex items-center justify-center font-bold text-xl transition bg-gray-100 text-gray-600 active:scale-90 hover:bg-gray-200"
            >
                ＋
            </button>
            <div className="text-[2.5rem] w-16 text-center font-black leading-none text-gray-800">
                {value}
            </div>
            <button
                onClick={onMinus}
                disabled={minusDisabled}
                className={`w-11 h-11 rounded-full flex items-center justify-center font-bold text-xl transition ${minusDisabled ? 'bg-gray-50 text-gray-300 cursor-not-allowed' : 'bg-gray-100 text-gray-600 active:scale-90 hover:bg-gray-200'}`}
            >
                －
            </button>
        </div>
    );
};

export const StepBettingHalfTime = ({
                                        htScore,
                                        finalScore,
                                        matchScore = {team1: 0, team2: 0},
                                        homeTeam,
                                        awayTeam,
                                        onScoreChange,
                                        onValidate,
                                        onBack = null,
                                        gains = null,
                                    }) => {
    const {t} = useTranslation();
    const isDraw = htScore.team1 === htScore.team2;

    return (
        <div className="mt-8 animate-fade-in pb-4">
            <div className="text-center mb-5">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 mb-2 rounded-full bg-indigo-50 border border-indigo-100">
                    <span className="text-sm leading-none">⏱️</span>
                    <span className="text-[10px] font-black text-indigo-500 uppercase tracking-widest">
                        {t('half_time_betting.badge', 'Prono mi-temps')}
                    </span>
                </div>
                <h2 className="text-xl font-black text-gray-800 leading-tight">
                    {t('half_time_betting.title', 'Le score à la pause ?')}
                </h2>
            </div>

            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 mb-4">
                <div className="flex justify-center items-start gap-8">
                    <TeamPicker
                        label={homeTeam?.shortName || homeTeam?.name}
                        logoUrl={homeTeam?.logoUrl}
                        value={htScore.team1}
                        min={matchScore.team1 || 0}
                        max={finalScore.team1}
                        onPlus={() => onScoreChange('team1', 'plus')}
                        onMinus={() => onScoreChange('team1', 'minus')}
                    />
                    <div className="h-12 w-px bg-gray-200 mx-2 mt-8"></div>
                    <TeamPicker
                        label={awayTeam?.shortName || awayTeam?.name}
                        logoUrl={awayTeam?.logoUrl}
                        value={htScore.team2}
                        min={matchScore.team2 || 0}
                        max={finalScore.team2}
                        onPlus={() => onScoreChange('team2', 'plus')}
                        onMinus={() => onScoreChange('team2', 'minus')}
                    />
                </div>

                {/* Gain potentiel mi-temps : version COMPACTE (pas la grosse carte du final). */}
                {gains && (
                    <div className="mt-5 pt-4 border-t border-gray-100">
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex flex-col gap-1.5">
                                <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-slate-500">
                                    <span>{isDraw ? '🤝' : '🏆'}</span>
                                    {isDraw
                                        ? t('half_time_betting.gain_draw', 'Bon nul à la pause')
                                        : t('half_time_betting.gain_win', 'Bon résultat à la pause')}
                                    <span className="font-black text-win-blue">+{gains.win}</span>
                                </span>
                                <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-slate-500">
                                    <span>🎯</span>
                                    {t('half_time_betting.gain_exact', 'Score exact à la pause')}
                                    <span className="font-black text-exact-gold">+{gains.score}</span>
                                </span>
                            </div>
                            <div className="text-right shrink-0">
                                <span className="block text-[9px] font-bold text-gray-400 uppercase tracking-wider">
                                    {t('betting.gain_total', 'Gain potentiel')}
                                </span>
                                <span className="text-2xl font-black text-brand-dark leading-none">
                                    +{gains.total}
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Rappel du prono final déjà posé : le couplage est visible */}
                <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-center gap-2 text-xs">
                    <span className="text-gray-400 font-bold uppercase tracking-wider">
                        {t('half_time_betting.final_context', 'Ton prono final')}
                    </span>
                    <span className="font-black text-gray-700 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded font-mono">
                        {finalScore.team1}-{finalScore.team2}
                    </span>
                </div>
            </div>

            <p className="text-center text-[11px] text-gray-400 font-medium mb-4 px-6">
                {t('half_time_betting.hint', 'À la pause, le score ne peut pas dépasser ton prono final.')}
            </p>

            <button
                onClick={onValidate}
                className="w-full py-4 bg-action-green text-white font-bold rounded-xl text-lg shadow-lg hover:brightness-110 active:scale-98 transition-transform"
            >
                {t('half_time_betting.validate', 'Valider mon prono mi-temps')}
            </button>

            {/* Secondaire discret (ghost) : se désister de la mi-temps. Même registre que le
                « Plus tard » de la modale d'upsell. */}
            {onBack && (
                <button
                    onClick={onBack}
                    className="w-full py-3 mt-1 text-gray-400 hover:text-gray-600 font-bold text-sm uppercase tracking-widest transition-colors"
                >
                    {t('half_time_betting.skip', 'Je ne joue pas la mi-temps')}
                </button>
            )}
        </div>
    );
};

export default StepBettingHalfTime;
